/**
 * 口袋管理器
 * 管理暂时存放起来的碎片
 */
import { PuzzlePiece } from './PuzzlePiece';
import { PieceGroup } from './PieceGroup';
import { UIManager } from '../ui/UIManager';
import { WebGLRenderer } from '../render/WebGLRenderer';
import { PuzzleBoard } from './PuzzleBoard';
import { Vector2 } from '../types';

export interface PocketItem {
    id: string;          // 唯一标识 (pieceId 或 groupId)
    type: 'piece' | 'group';
    mainPieceId: string; // 用于获取缩略图的碎片ID
    pieceCount: number;
    thumbnail: string;
}

export class PocketManager {
    private items: Map<string, PocketItem> = new Map();
    private board: PuzzleBoard;
    private renderer: WebGLRenderer;
    private uiElements: {
        pocketBtn: HTMLElement;
        pocketCount: HTMLElement;
        pocketPanel: HTMLElement;
        pocketGrid: HTMLElement;
        btnClose: HTMLElement;
    };

    private onExtract?: (itemId: string, screenPos: Vector2) => void;

    constructor(board: PuzzleBoard, renderer: WebGLRenderer) {
        this.board = board;
        this.renderer = renderer;

        this.uiElements = {
            pocketBtn: document.getElementById('btn-pocket')!,
            pocketCount: document.getElementById('pocket-count')!,
            pocketPanel: document.getElementById('pocket-panel')!,
            pocketGrid: document.getElementById('pocket-grid')!,
            btnClose: document.getElementById('btn-close-pocket')!
        };

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.uiElements.pocketBtn.onclick = (e) => {
            e.stopPropagation();
            this.togglePanel();
        };
        this.uiElements.btnClose.onclick = () => this.togglePanel(false);

        // 点击外部收回
        document.addEventListener('pointerdown', (e) => {
            const isPanelOpen = !this.uiElements.pocketPanel.classList.contains('translate-x-full');
            if (!isPanelOpen) return;

            const target = e.target as HTMLElement;
            // 如果点击的是侧边栏内部、按钮本身，则不关闭
            const isInsidePanel = this.uiElements.pocketPanel.contains(target);
            const isBtn = this.uiElements.pocketBtn.contains(target);
            if (isInsidePanel || isBtn) return;

            // 检查是否点击到了主画布上的碎片
            const rect = this.renderer.getApp().view.getBoundingClientRect();
            const screenPos = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
            const logicPos = this.renderer.screenToLogic(screenPos);
            const hitPiece = this.board.findPieceAtPosition(logicPos);

            // 只有当点击的既不是面板也不是碎片时，才关闭
            if (!hitPiece) {
                this.togglePanel(false);
            }
        });
    }

    /**
     * 设置取出回调
     */
    setOnExtract(callback: (itemId: string, screenPos: Vector2) => void): void {
        this.onExtract = callback;
    }

    /**
     * 将碎片或组存入口袋
     */
    addItem(pieceId: string): void {
        const piece = this.board.getPiece(pieceId);
        if (!piece) return;

        const group = this.board.getGroupByPiece(piece);
        const itemId = group ? group.id : piece.id;

        if (this.items.has(itemId)) return;

        const pieces = group ? group.getPieces() : [piece];
        const thumbnail = this.renderer.getPieceThumbnail(piece.id) || '';

        const item: PocketItem = {
            id: itemId,
            type: group ? 'group' : 'piece',
            mainPieceId: piece.id,
            pieceCount: pieces.length,
            thumbnail: thumbnail
        };

        // 1. 立即更新逻辑状态和 UI 数据
        pieces.forEach(p => p.setIsInPocket(true));
        this.items.set(itemId, item);
        this.updateUI();

        // 2. 异步播放动画，不再阻塞 UI 刷新
        const btnRect = this.uiElements.pocketBtn.getBoundingClientRect();
        const targetPos = {
            x: btnRect.left + btnRect.width / 2,
            y: btnRect.top + btnRect.height / 2
        };

        pieces.forEach(p => {
            this.renderer.animateToPocket(p.id, targetPos);
        });
    }

    /**
     * 静默恢复口袋项目（用于存档加载）
     */
    restoreItem(pieceId: string): void {
        const piece = this.board.getPiece(pieceId);
        if (!piece) return;

        const group = this.board.getGroupByPiece(piece);
        const itemId = group ? group.id : piece.id;

        if (this.items.has(itemId)) return;

        const pieces = group ? group.getPieces() : [piece];
        const thumbnail = this.renderer.getPieceThumbnail(piece.id) || '';

        const item: PocketItem = {
            id: itemId,
            type: group ? 'group' : 'piece',
            mainPieceId: piece.id,
            pieceCount: pieces.length,
            thumbnail: thumbnail
        };

        pieces.forEach(p => p.setIsInPocket(true));
        this.items.set(itemId, item);
        this.updateUI();
    }

    /**
     * 设置侧边栏穿透状态（用于拖拽碎片时）
     */
    setPassthrough(enabled: boolean): void {
        if (enabled) {
            this.uiElements.pocketPanel.style.pointerEvents = 'none';
            this.uiElements.pocketPanel.style.opacity = '0.4';
            // 暂时移除模糊效果以提升性能
            this.uiElements.pocketPanel.classList.remove('backdrop-blur');
        } else {
            this.uiElements.pocketPanel.style.pointerEvents = 'auto';
            this.uiElements.pocketPanel.style.opacity = '1';
            this.uiElements.pocketPanel.classList.add('backdrop-blur');
        }
    }

    /**
     * 获取面板区域
     */
    getPanelRect(): DOMRect {
        return this.uiElements.pocketPanel.getBoundingClientRect();
    }

    /**
     * 获取按钮区域
     */
    getBtnRect(): DOMRect {
        return this.uiElements.pocketBtn.getBoundingClientRect();
    }

    /**
     * 切换面板显示
     */
    togglePanel(show?: boolean): void {
        const isShow = show !== undefined ? show : this.uiElements.pocketPanel.classList.contains('translate-x-full');
        if (isShow) {
            this.uiElements.pocketPanel.classList.remove('translate-x-full');
            this.renderGrid();
        } else {
            this.uiElements.pocketPanel.classList.add('translate-x-full');
        }
    }

    /**
     * 更新气泡数量
     */
    private updateUI(): void {
        const count = this.items.size;
        this.uiElements.pocketCount.textContent = count.toString();
        
        if (count > 0) {
            this.uiElements.pocketCount.classList.remove('scale-0');
            this.uiElements.pocketCount.classList.add('scale-100');
        } else {
            this.uiElements.pocketCount.classList.remove('scale-100');
            this.uiElements.pocketCount.classList.add('scale-0');
        }

        if (!this.uiElements.pocketPanel.classList.contains('translate-x-full')) {
            this.renderGrid();
        }
    }

    /**
     * 渲染网格内容
     */
    private renderGrid(): void {
        this.uiElements.pocketGrid.innerHTML = '';
        
        this.items.forEach(item => {
            const div = document.createElement('div');
            div.className = 'relative aspect-square bg-white rounded-xl border border-stone-200 shadow-sm hover:shadow-md hover:border-stone-300 transition-all cursor-grab active:cursor-grabbing group overflow-hidden flex items-center justify-center p-2';
            
            const img = document.createElement('img');
            img.src = item.thumbnail;
            img.className = 'max-w-full max-h-full object-contain transition-transform group-hover:scale-110';
            img.draggable = false; // 我们手动处理拖拽
            
            if (item.pieceCount > 1) {
                const badge = document.createElement('div');
                badge.className = 'absolute bottom-1 right-1 bg-stone-800 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold';
                badge.textContent = item.pieceCount.toString();
                div.appendChild(badge);
            }

            // 处理从侧边栏拖拽出的逻辑
            div.onpointerdown = (e) => {
                this.handleItemDragStart(item.id, e);
            };

            div.appendChild(img);
            this.uiElements.pocketGrid.appendChild(div);
        });
    }

    /**
     * 处理从网格拖拽出的交互
     */
    private handleItemDragStart(itemId: string, e: PointerEvent): void {
        // 关闭面板并开始在主画布的拖拽
        this.togglePanel(false);
        
        const item = this.items.get(itemId);
        if (!item) return;

        // 从口袋移除
        this.items.delete(itemId);
        this.updateUI();

        // 逻辑层取出：恢复状态
        const piece = this.board.getPiece(item.mainPieceId);
        if (piece) {
            const group = this.board.getGroupByPiece(piece);
            const pieces = group ? group.getPieces() : [piece];
            pieces.forEach(p => {
                p.setIsInPocket(false);
                this.renderer.syncPieceState(p);
            });
        }

        // 通知外部（PuzzleGame）该 itemId 的碎片现在要在鼠标位置开始拖拽
        this.onExtract?.(item.mainPieceId, { x: e.clientX, y: e.clientY });
    }

    /**
     * 获取当前所有口袋项目的 ID (用于同步存档)
     */
    getItemIds(): string[] {
        return Array.from(this.items.keys());
    }

    /**
     * 清空
     */
    clear(): void {
        this.items.clear();
        this.updateUI();
    }
}
